// src/components/AddInvestmentModal.tsx: add or edit a position in the
// allocator's private portfolio tracker.
//
// The manager picker searches the allocator's approved connections, so a
// position can be linked to a real fund and pick up platform performance data.
// Anything not on the platform can still be entered by hand, it just has no
// fund_id and therefore no YTD figure.
import React, { useEffect, useMemo, useState } from 'react';
import { supabase } from '../services/supabase';
import { C } from '../lib/applications';
import { CURRENCIES, Investment } from '../lib/portfolioCalc';

const overlay: React.CSSProperties = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
  display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
  padding: '40px 20px', zIndex: 300, overflowY: 'auto',
};
const inputStyle: React.CSSProperties = {
  width: '100%', background: C.white, border: `1px solid ${C.border}`,
  borderRadius: '3px', color: C.text, fontSize: '13px', padding: '9px 11px',
  outline: 'none', fontFamily: 'system-ui', boxSizing: 'border-box',
};
const labelStyle: React.CSSProperties = {
  fontSize: '10px', fontWeight: 700, color: C.muted, letterSpacing: '0.6px',
  textTransform: 'uppercase', display: 'block', marginBottom: '5px',
};
const btnPrimary: React.CSSProperties = {
  background: C.gold, color: C.navy, border: 'none', borderRadius: '3px',
  padding: '10px 22px', fontSize: '13px', fontWeight: 700, cursor: 'pointer', fontFamily: 'system-ui',
};
const btnGhost: React.CSSProperties = {
  background: C.white, color: C.textSec, border: `1px solid ${C.border}`,
  borderRadius: '3px', padding: '10px 18px', fontSize: '13px', fontWeight: 600,
  cursor: 'pointer', fontFamily: 'system-ui',
};

interface ConnectedFund {
  fundId: string | null;
  fundName: string;
  firmId: string;
  firmName: string;
}

const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div style={{ marginBottom: '14px' }}>
    <label style={labelStyle}>{label}</label>
    {children}
  </div>
);

export default function AddInvestmentModal({
  allocatorProfileId, allocatorFirmId, existing, onClose, onSaved,
}: {
  allocatorProfileId: string;
  allocatorFirmId: string | null;
  existing?: Investment | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isEdit = !!existing;

  const [options, setOptions] = useState<ConnectedFund[]>([]);
  const [search, setSearch] = useState('');
  const [manual, setManual] = useState(false);
  const [picked, setPicked] = useState<ConnectedFund | null>(null);

  const [managerName, setManagerName] = useState(existing?.manager_name || '');
  const [fundName, setFundName]       = useState(existing?.fund_name || '');
  const [amount, setAmount]           = useState(existing ? String(existing.amount_invested) : '');
  const [currency, setCurrency]       = useState(existing?.currency || 'USD');
  const [date, setDate]               = useState(existing?.investment_date || new Date().toISOString().slice(0, 10));
  const [currentValue, setCurrentValue] = useState(existing?.current_value != null ? String(existing.current_value) : '');
  const [notes, setNotes]             = useState(existing?.notes || '');

  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');

  // An existing position that was entered by hand stays in manual mode.
  useEffect(() => {
    if (existing && !existing.fund_id) setManual(true);
  }, [existing]);

  // Approved connections give the funds this allocator can link to.
  useEffect(() => {
    if (!allocatorFirmId) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase.from('connections')
        .select('manager_firm:firms!connections_manager_firm_id_fkey(id, name)')
        .eq('allocator_firm_id', allocatorFirmId).eq('status', 'approved');
      if (cancelled) return;
      const firms = (data || [])
        .map((c: any) => (Array.isArray(c.manager_firm) ? c.manager_firm[0] : c.manager_firm))
        .filter(Boolean);
      const uniqueFirms = Array.from(new Map(firms.map((f: any) => [f.id, f])).values()) as any[];
      if (uniqueFirms.length === 0) { setOptions([]); return; }

      const { data: funds } = await supabase.from('funds')
        .select('id, name, firm_id').in('firm_id', uniqueFirms.map(f => f.id));
      if (cancelled) return;

      const rows: ConnectedFund[] = [];
      uniqueFirms.forEach(firm => {
        const firmFunds = (funds || []).filter((fn: any) => fn.firm_id === firm.id);
        if (firmFunds.length === 0) {
          // Connected manager with no listed fund: still selectable, no fund_id.
          rows.push({ fundId: null, fundName: '', firmId: firm.id, firmName: firm.name });
        } else {
          firmFunds.forEach((fn: any) =>
            rows.push({ fundId: fn.id, fundName: fn.name, firmId: firm.id, firmName: firm.name }));
        }
      });
      setOptions(rows);
    })();
    return () => { cancelled = true; };
  }, [allocatorFirmId]);

  const matches = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return options.slice(0, 8);
    return options
      .filter(o => `${o.firmName} ${o.fundName}`.toLowerCase().includes(q))
      .slice(0, 8);
  }, [options, search]);

  const choose = (o: ConnectedFund) => {
    setPicked(o);
    setManagerName(o.firmName);
    setFundName(o.fundName || o.firmName);
    setSearch('');
  };

  const canSave = managerName.trim() && fundName.trim() && parseFloat(amount) > 0 && date;

  const save = async () => {
    setSaving(true); setError('');
    const payload = {
      allocator_profile_id: allocatorProfileId,
      allocator_firm_id: allocatorFirmId,
      manager_firm_id: manual ? null : (picked?.firmId ?? existing?.manager_firm_id ?? null),
      fund_id: manual ? null : (picked?.fundId ?? existing?.fund_id ?? null),
      manager_name: managerName.trim(),
      fund_name: fundName.trim(),
      amount_invested: parseFloat(amount),
      currency,
      investment_date: date,
      current_value: currentValue ? parseFloat(currentValue) : null,
      notes: notes.trim() || null,
      updated_at: new Date().toISOString(),
    };

    const { error: err } = isEdit
      ? await supabase.from('allocator_investments').update(payload).eq('id', existing!.id)
      : await supabase.from('allocator_investments').insert({ ...payload, is_active: true });

    setSaving(false);
    if (err) { setError(err.message); return; }
    onSaved();
  };

  return (
    <div onClick={e => { if (e.target === e.currentTarget) onClose(); }} style={overlay}>
      <div style={{ background: C.white, borderRadius: '4px', width: '100%', maxWidth: '540px', boxShadow: '0 20px 60px rgba(0,0,0,0.3)', overflow: 'hidden' }}>

        {/* Navy header */}
        <div style={{ background: C.navy, padding: '18px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px' }}>
          <div>
            <div style={{ fontSize: '9.5px', color: C.gold, letterSpacing: '1.6px', fontWeight: 700, textTransform: 'uppercase', marginBottom: '4px' }}>
              Private portfolio
            </div>
            <h2 style={{ fontFamily: 'Georgia, "Times New Roman", serif', fontSize: '19px', color: C.white, fontWeight: 400, margin: 0 }}>
              {isEdit ? 'Edit investment' : 'Add investment'}
            </h2>
          </div>
          <button onClick={onClose} aria-label="Close" style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.6)', fontSize: '22px', cursor: 'pointer', lineHeight: 1 }}>×</button>
        </div>

        <div style={{ padding: '22px 24px' }}>
          {error && (
            <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '3px', padding: '9px 12px', marginBottom: '16px', fontSize: '12.5px', color: C.danger }}>{error}</div>
          )}

          {/* Manager / fund */}
          {!manual ? (
            <Field label="Manager or fund">
              <input
                style={inputStyle}
                value={picked ? `${picked.firmName}${picked.fundName ? ` — ${picked.fundName}` : ''}` : search}
                placeholder="Search your connected managers…"
                onChange={e => { setSearch(e.target.value); setPicked(null); }}
              />
              {!picked && (
                <div style={{ border: `1px solid ${C.border}`, borderTop: 'none', borderRadius: '0 0 3px 3px', maxHeight: '190px', overflowY: 'auto' }}>
                  {matches.length === 0 ? (
                    <div style={{ padding: '12px 13px', fontSize: '12.5px', color: C.muted }}>
                      {options.length === 0
                        ? 'No approved connections yet.'
                        : 'No match in your connections.'}
                    </div>
                  ) : matches.map((o, i) => (
                    <div key={`${o.firmId}-${o.fundId ?? i}`} onClick={() => choose(o)}
                      style={{ padding: '9px 13px', cursor: 'pointer', borderBottom: `1px solid ${C.borderLt}` }}
                      onMouseEnter={e => (e.currentTarget.style.background = C.surface)}
                      onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
                      <div style={{ fontSize: '13px', color: C.text, fontWeight: 500 }}>{o.firmName}</div>
                      <div style={{ fontSize: '11.5px', color: C.muted }}>{o.fundName || 'No fund listed'}</div>
                    </div>
                  ))}
                </div>
              )}
              <button onClick={() => { setManual(true); setPicked(null); }}
                style={{ background: 'none', border: 'none', color: C.teal, fontSize: '12px', fontWeight: 600, cursor: 'pointer', padding: '8px 0 0', fontFamily: 'system-ui' }}>
                Manager not on the platform? Add manually
              </button>
            </Field>
          ) : (
            <>
              <Field label="Manager firm name *">
                <input style={inputStyle} value={managerName} onChange={e => setManagerName(e.target.value)} placeholder="Meridian Capital" />
              </Field>
              <Field label="Fund name *">
                <input style={inputStyle} value={fundName} onChange={e => setFundName(e.target.value)} placeholder="Meridian Global Opportunities" />
              </Field>
              <button onClick={() => { setManual(false); setManagerName(''); setFundName(''); }}
                style={{ background: 'none', border: 'none', color: C.teal, fontSize: '12px', fontWeight: 600, cursor: 'pointer', padding: '0 0 14px', fontFamily: 'system-ui' }}>
                Search my connections instead
              </button>
              <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: '3px', padding: '10px 12px', marginBottom: '14px', fontSize: '12px', color: C.muted, lineHeight: 1.6 }}>
                A manually entered fund has no platform performance data, so it will show
                <strong> --</strong> for YTD return and is excluded from the weighted portfolio figure.
              </div>
            </>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '12px' }}>
            <Field label="Amount invested *">
              <input style={inputStyle} type="number" min="0" step="any" value={amount}
                onChange={e => setAmount(e.target.value)} placeholder="5000000" />
            </Field>
            <Field label="Currency">
              <select style={{ ...inputStyle, cursor: 'pointer' }} value={currency} onChange={e => setCurrency(e.target.value)}>
                {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <Field label="Investment date *">
              <input style={inputStyle} type="date" value={date} onChange={e => setDate(e.target.value)} />
            </Field>
            <Field label="Current value (optional)">
              <input style={inputStyle} type="number" min="0" step="any" value={currentValue}
                onChange={e => setCurrentValue(e.target.value)} placeholder="Leave blank if unknown" />
            </Field>
          </div>

          <Field label="Notes (optional)">
            <textarea style={{ ...inputStyle, height: '80px', resize: 'vertical', lineHeight: 1.6 }}
              value={notes} onChange={e => setNotes(e.target.value)} />
          </Field>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '6px' }}>
            <button onClick={onClose} style={btnGhost} disabled={saving}>Cancel</button>
            <button onClick={save} disabled={!canSave || saving}
              style={{ ...btnPrimary, opacity: canSave && !saving ? 1 : 0.5, cursor: canSave && !saving ? 'pointer' : 'not-allowed' }}>
              {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Add investment'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
